import {
  describeCondition,
  isShadowRule,
  labeebRulesOf,
  labeebRulesPath,
  type LabeebRule,
  type LabeebRules,
} from "../../api/labeeb";
import type { AppConfig } from "../../config";
import { useResource } from "../../hooks/useResource";
import { CouldNotLook, Denied, LoadFailed, Loading, SignedOut } from "../states";
import { Badge, Card, Empty, Notice, Table, Td, Th } from "../ui";
import { ProductTag } from "../../products/PerProduct";

/**
 * This business's autonomy rules — what the assistant may do unattended.
 *
 * READS ONLY, deliberately. The editor is a separate piece of work: a rule
 * carries a condition language (six kinds), a scope of target plus operations,
 * a subject, triggers and a mode, and a half-understood editor for that is
 * worse than none. Showing the rules first means the shape can be reviewed
 * against real data before anything writes.
 *
 * WHAT AN OPERATOR NEEDS FROM THIS TABLE, in order:
 *
 *   - which rules EXIST, because with no enforcing rule every write needs
 *     approval and reads never do. An empty list is a meaningful answer, not
 *     a gap, so it says that rather than showing nothing.
 *   - whether each one DECIDES anything. A `shadow` rule is evaluated and
 *     recorded as what it would have done, and changes nothing — it is also
 *     how a rule is retired, since this API has no delete. A shadow rule read
 *     as live is the single most misleading thing this table could do, so it
 *     is badged, not merely labelled in a column.
 *   - what it decides and on what. `effect` is the decision; strictest
 *     matching rule wins (deny > approve > auto).
 *
 * `version` is shown because every change sends it back as
 * `expected_version`, so it is the number an operator quotes when a write is
 * refused for being stale.
 */
export function LabeebRules({ config, tenantId }: { config: AppConfig; tenantId: string }) {
  const rules = useResource<LabeebRules>(config, labeebRulesPath(tenantId), [tenantId]);
  const s = rules.state;

  return (
    <Card
      title="Autonomy rules"
      aside={<ProductTag id="labeeb" />}
      note="What the assistant may do without asking"
      collapsible
    >
      {s.kind === "loading" && <Loading what="autonomy rules" />}
      {s.kind === "signedout" && <SignedOut />}
      {s.kind === "denied" && <Denied message={s.error.message} />}
      {s.kind === "unavailable" && (
        <CouldNotLook what="autonomy rules" message={s.error.message} onRetry={rules.reload} />
      )}
      {s.kind === "notfound" && (
        // A 404 here means Labeeb has no record of this business — it never
        // creates a phantom tenant, so this is "not registered", not "no
        // rules". Saying "no rules" would imply the assistant is running
        // unconstrained for a merchant Labeeb has never seen.
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Labeeb has no record of this merchant, so it holds no rules for them. A business
          appears once somebody at it signs in.
        </p>
      )}
      {(s.kind === "invalid" || s.kind === "error") && (
        <LoadFailed what="autonomy rules" message={s.error.message} />
      )}

      {s.kind === "ready" &&
        (() => {
          const items = labeebRulesOf(s.data);

          if (items.length === 0) {
            return (
              <>
                <Empty>No autonomy rules.</Empty>
                {/* THE DEFAULT IS NOT "ANYTHING GOES". Without an enforcing
                    rule every write needs approval, which is the safe end of
                    the range — but an operator reading an empty table could
                    conclude either, so it is stated. */}
                <p className="mt-2 text-xs/5 text-slate-500 dark:text-slate-400">
                  With no enforcing rule, every write needs approval. Reads never do.
                </p>
              </>
            );
          }

          const enforcing = items.filter((r) => !isShadowRule(r)).length;

          return (
            <>
              {enforcing === 0 && (
                <div className="mb-3">
                  <Notice tone="amber" title="Nothing here is enforced">
                    Every rule below is observing only, so every write still needs approval.
                  </Notice>
                </div>
              )}

              <Table
                head={
                  <tr>
                    <Th>Rule</Th>
                    <Th>Applies to</Th>
                    <Th>Decides</Th>
                    <Th>Mode</Th>
                    <Th>Triggers</Th>
                    <Th right>Version</Th>
                  </tr>
                }
              >
                {items.map((r) => (
                  <RuleRow key={r.rule_id ?? JSON.stringify(r.rule)} rule={r} />
                ))}
              </Table>

              <p className="mt-3 text-xs/5 text-slate-500 dark:text-slate-400">
                The strictest matching rule wins: deny beats approve beats auto. A rule is
                retired by setting it to observing — there is no delete.
              </p>
            </>
          );
        })()}
    </Card>
  );
}

/** One rule. Its conditions are what make it specific, so they are shown. */
function RuleRow({ rule }: { rule: LabeebRule }) {
  const d = rule.rule;
  const ops = d?.scope?.ops ?? [];
  const conditions = d?.conditions ?? [];
  const shadow = isShadowRule(rule);

  return (
    <tr>
      <Td>
        <span className="ref">{rule.rule_id ?? d?.id ?? "—"}</span>
        {/* The rule author's own sentence, which is usually a better
            explanation than the scope can be. */}
        {d?.reason && (
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{d.reason}</p>
        )}
      </Td>
      <Td>
        <span className="ref">{d?.scope?.target ?? "—"}</span>
        {ops.length > 0 && (
          <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
            {ops.join(", ")}
          </span>
        )}
        {/* `branches: null` means every branch, which is NOT the same as an
            empty list — and it is the wider of the two, so it is said. */}
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
          {d?.scope?.branches == null
            ? "all branches"
            : d.scope.branches.length === 0
              ? "no branches"
              : `${d.scope.branches.length} branch(es)`}
        </p>
        {conditions.length > 0 && (
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            when {conditions.map(describeCondition).join(" · ")}
          </p>
        )}
      </Td>
      <Td>
        {d?.effect === "deny" ? (
          <Badge tone="red">deny</Badge>
        ) : d?.effect === "approve" ? (
          <Badge tone="amber">approve</Badge>
        ) : d?.effect === "auto" ? (
          <Badge>auto</Badge>
        ) : (
          <span className="text-xs text-slate-500 dark:text-slate-400">{d?.effect ?? "—"}</span>
        )}
      </Td>
      <Td>
        {shadow ? (
          <div>
            <Badge tone="amber">observing only</Badge>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">not enforced</p>
          </div>
        ) : (
          <Badge>enforce</Badge>
        )}
      </Td>
      <Td>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {(d?.triggers ?? []).join(", ") || "—"}
        </span>
      </Td>
      <Td right>
        <span className="tabular-nums">{rule.version ?? "—"}</span>
      </Td>
    </tr>
  );
}
